import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EntityInfo } from './entity-info';
import { ValidationContext, ValidationContextBase } from './validation-context';
import { Validator } from './validator';
import { ValidatorFactory } from './validator-base';

export interface ValidatorConfig {
  name: string;
  context?: Partial<ValidationContextBase>;
  async?: boolean;
  condition?: string;
}

export interface ValidatorGroupConfig {
  group: string;
  validators: ValidatorConfigLike[];
}

export type ValidatorConfigLike = string | ValidatorConfig;

export type ValidatorOrGroupConfig = ValidatorConfigLike | ValidatorGroupConfig;

export interface PropertyValidationConfig {
  readonly displayName?: string;
  readonly required?: boolean;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly validators?: ValidatorOrGroupConfig[];
}

export interface PropertyValidationConfigMap {
  [propertyName: string]: PropertyValidationConfig;
}

export interface ModelValidationConfig {
  readonly extends?: string;
  readonly validators?: ValidatorOrGroupConfig[];
  readonly properties?: PropertyValidationConfigMap;
  readonly condition?: string;
}

export interface GlobalMetadataConfig {
  validators?: ValidatorOrGroupConfig[];
}

export interface ModelValidationMetadataMap {
  [modelName: string]: ModelValidationConfig;
  global?: GlobalMetadataConfig;
}

export const isValidatorGroupConfig = (
  config: any
): config is ValidatorGroupConfig => !!config.group;

/**
 * Parses an expression and expands optional chaining
 *
 * @param expression
 */
export const parseOptionalChaining = (expression: string): string => {
  const pattern = /([\w\[\]\.\?]+)\?.([[\w\[\]\.]+)/g;

  const replacer = (match: string, p1: string, p2: string): string =>
    `(${p1.replace(pattern, replacer)} || {}).${p2}`;

  return expression.replace(pattern, replacer);
};

export class ValidatorMetadata {
  readonly config: ValidatorConfig;
  private condition: (
    context: ValidationContext,
    model: any,
    isClient: boolean,
    isServer: boolean
  ) => boolean;

  constructor(
    config: ValidatorConfigLike,
    private validatorFactories: ValidatorFactory[],
    private groupConfig?: ValidatorGroupConfig
  ) {
    if (typeof config === 'string') {
      this.config = { name: config };
    } else {
      this.config = config;
    }
  }

  get name(): string {
    return this.config.name;
  }

  get context(): Partial<ValidationContextBase> {
    return this.config.context;
  }

  get async(): boolean {
    return !!this.config.async;
  }

  get group(): string {
    return this.groupConfig && this.groupConfig.group;
  }

  evalCondition(
    context: ValidationContext,
    contextResolver: (context: ValidationContext) => ValidationContext
  ): boolean {
    if (!this.config.condition) {
      return true;
    }

    const resolvedContext = contextResolver(context);
    try {
      /**
       * SECURITY: Safe because text passed to `Function` comes from our server, not the user
       */
      if (!this.condition) {
        const evalFunc = `function(context, model, isClient, isServer){ return ${parseOptionalChaining(
          this.config.condition
        )} }`;
        this.condition = Function(`"use strict";return (${evalFunc})`)();
      }
      return this.condition(
        resolvedContext,
        resolvedContext.model,
        resolvedContext.isClient,
        resolvedContext.isServer
      );
    } catch (e) {
      const message =
        `Condition '${this.config.condition}' failed for validator '${this.name}'` +
        ` and model type '${resolvedContext.modelType}' with error '${e.message}'`;
      throw new Error(message);
    }
  }

  getValidator(): Validator {
    const factories = this.validatorFactories.filter(
      (f) => f.name === this.name
    );
    if (factories.length === 0) {
      throw new Error(
        `ValidatorMetadata: No factory found with name '${this.name}'.`
      );
    }

    const validator = factories
      .map((factory) => factory.fn(this.group, this.context))
      .find((v) => v.isAsync === this.async);
    if (!validator) {
      if (this.async) {
        throw new Error(
          `ValidationService: No asynchronous validator with name '${this.name}' found.`
        );
      } else {
        throw new Error(
          `ValidationService: No synchronous validator with name '${this.name}'.`
        );
      }
    }
    validator.metadata = this;

    return validator;
  }
}

const asValidatorMetadata = (
  validators: ValidatorOrGroupConfig[],
  validatorFactories: ValidatorFactory[]
): ValidatorMetadata[] =>
  (validators || [])
    .map((config) => {
      if (isValidatorGroupConfig(config)) {
        return (config.validators || []).map(
          (validatorConfig) =>
            new ValidatorMetadata(validatorConfig, validatorFactories, config)
        );
      } else {
        return [new ValidatorMetadata(config, validatorFactories)];
      }
    })
    .reduce((prev, curr) => [...prev, ...curr], []);

export class GlobalMetadata {
  private _validators: ValidatorMetadata[];

  constructor(
    private configs: GlobalMetadataConfig[],
    private validatorFactories: ValidatorFactory[]
  ) {}

  get validators(): ValidatorMetadata[] {
    if (this._validators) {
      return this._validators;
    }

    const globalValidators = this.configs
      .map((config) => config.validators || [])
      .reduce((prev, curr) => [...prev, ...curr], []);
    this._validators = asValidatorMetadata(
      globalValidators || [],
      this.validatorFactories
    );
    return this._validators;
  }
}

export class PropertyMetadata {
  private _validators: ValidatorMetadata[];

  constructor(
    readonly propertyName: string,
    private config: PropertyValidationConfig,
    private validatorFactories: ValidatorFactory[]
  ) {}

  get displayName(): string {
    return this.config.displayName;
  }

  get validators(): ValidatorMetadata[] {
    if (this._validators) {
      return this._validators;
    }

    this._validators = asValidatorMetadata(
      this.config.validators || [],
      this.validatorFactories
    );
    // Add well-known default validators
    if (
      this.config.required &&
      !this._validators.some(
        (validator) => validator.name === 'required' && !validator.group
      )
    ) {
      this._validators.push(
        new ValidatorMetadata('required', this.validatorFactories)
      );
    }
    if (
      this.config.minLength &&
      !this._validators.some(
        (validator) => validator.name === 'minLength' && !validator.group
      )
    ) {
      const config = {
        name: 'minLength',
        context: {
          minLength: this.config.minLength,
        },
      };
      this._validators.push(
        new ValidatorMetadata(config, this.validatorFactories)
      );
    }
    if (
      this.config.maxLength &&
      !this._validators.some(
        (validator) => validator.name === 'maxLength' && !validator.group
      )
    ) {
      const config = {
        name: 'maxLength',
        context: {
          maxLength: this.config.maxLength,
        },
      };
      this._validators.push(
        new ValidatorMetadata(config, this.validatorFactories)
      );
    }

    return this._validators;
  }
}

export class ModelMetadata {
  private _validators: ValidatorMetadata[];
  private _properties: PropertyMetadata[];
  private condition: (
    entityInfos: EntityInfo[],
    model: any,
    isClient: boolean,
    isServer: boolean
  ) => boolean;

  constructor(
    readonly metadataStore: ValidationMetadata,
    readonly modelType: string,
    private config: ModelValidationConfig,
    private validatorFactories: ValidatorFactory[],
    private globalMetadata: GlobalMetadata
  ) {}

  get validators(): ValidatorMetadata[] {
    if (this._validators) {
      return this._validators;
    }

    this._validators = [...this.globalMetadata.validators];
    if (this.config.extends) {
      const parent = this.metadataStore.getModelConfig(this.config.extends);
      if (parent) {
        this._validators = [...this._validators, ...parent.validators];
      }
    }
    this._validators = [
      ...this._validators,
      ...asValidatorMetadata(
        this.config.validators || [],
        this.validatorFactories
      ),
    ];
    return this._validators;
  }

  get properties(): PropertyMetadata[] {
    if (this._properties) {
      return this._properties;
    }

    this._properties = [];
    if (this.config.extends) {
      const parent = this.metadataStore.getModelConfig(this.config.extends);
      if (parent) {
        this._properties = parent.properties;
      }
    }
    const properties = Object.keys(this.config.properties || {}).map(
      (propertyName) =>
        new PropertyMetadata(
          propertyName,
          this.config.properties[propertyName],
          this.validatorFactories
        )
    );
    this._properties = [...this._properties, ...properties];
    return this._properties;
  }

  getAsyncProperties(): PropertyMetadata[] {
    return this.properties.filter((property) =>
      property.validators.some((validator) => validator.async)
    );
  }

  evalCondition(
    entityInfos: EntityInfo[],
    model: any,
    contextResolver: (context: ValidationContext) => ValidationContext
  ): boolean {
    const parent =
      this.config.extends &&
      this.metadataStore.getModelConfig(this.config.extends);
    const condition =
      this.config.condition || (parent && parent.config.condition);

    if (!condition) {
      return true;
    }

    const resolvedContext = contextResolver({
      model,
      modelType: this.modelType,

      // Just to meet a full context
      metadata: this,
      isClient: true,
      isServer: false,
      messageTemplate: '',
    });
    try {
      /**
       * SECURITY: Safe because text passed to `Function` comes from our server, not the user
       */
      if (!this.condition) {
        const entityHelpers = [
          `function findEntity(id, type) {
            var ei = entityInfos.find(function(entityInfo) {
              return entityInfo.entity.id === id && entityInfo.entityType === type;
            });
            return ei && ei.entity;
          }`,
        ].join('\n\n');
        const evalFunc = `function(entityInfos, model, isClient, isServer){ return ${parseOptionalChaining(
          condition
        )}; ${entityHelpers} }`;
        this.condition = Function(`"use strict";return (${evalFunc})`)();
      }
      return this.condition(entityInfos, resolvedContext.model, true, false);
    } catch (e) {
      const message = `Condition '${condition}' failed for model type '${resolvedContext.modelType}' with error '${e.message}'`;
      throw new Error(message);
    }
  }
}

export type ModelValidationMetadataLike = (
  | ModelValidationMetadataMap
  | ModelValidationMetadataMap[]
)[];

export const VALIDATION_ENTITY_METADATA =
  new InjectionToken<ModelValidationMetadataLike>('ValidationEntityMetadata');
export const VALIDATOR_FACTORY = new InjectionToken<ValidatorFactory[]>(
  'ValidationFactory'
);

@Injectable({ providedIn: 'root' })
export class ValidationMetadata {
  private metadata: ModelValidationMetadataMap[];
  private modelMetadata: ModelMetadata[];
  private globalMetadata: GlobalMetadata;
  private metadataChanged = new Subject<ValidationMetadata>();

  constructor(
    @Optional()
    @Inject(VALIDATION_ENTITY_METADATA)
    validationMetadata: ModelValidationMetadataLike,
    @Optional()
    @Inject(VALIDATOR_FACTORY)
    private validatorFactories: ValidatorFactory[]
  ) {
    this.setMetadata(validationMetadata, true);
    this.validatorFactories = this.validatorFactories || [];
  }

  get metadataChanged$(): Observable<ValidationMetadata> {
    return this.metadataChanged.asObservable();
  }

  getModelConfig(modelType: string): ModelMetadata {
    let config = this.getModelConfigs().find((c) => c.modelType === modelType);
    if (!config) {
      // Create config with global validators only
      config = new ModelMetadata(
        this,
        modelType,
        {},
        this.validatorFactories,
        this.getGlobalConfig()
      );
      this.modelMetadata.push(config);
    }

    return config;
  }

  getModelConfigs(): ModelMetadata[] {
    return (
      this.modelMetadata || (this.modelMetadata = this.getModelConfigsCore())
    );
  }

  getGlobalConfig(): GlobalMetadata {
    return (
      this.globalMetadata ||
      (this.globalMetadata = new GlobalMetadata(
        this.metadata.map((metadata) => metadata.global || {}),
        this.validatorFactories
      ))
    );
  }

  setMetadata(
    validationMetadata: ModelValidationMetadataLike,
    suppressChangeEvent: boolean = false
  ): void {
    this.clear();

    this.metadata = (validationMetadata || [])
      .map((item) => (Array.isArray(item) ? item : [item]))
      .reduce((prev, curr) => [...prev, ...curr], []);

    if (!suppressChangeEvent) {
      this.metadataChanged.next(this);
    }
  }

  importMetadata(
    validationMetadata: ModelValidationMetadataLike,
    suppressChangeEvent: boolean = false
  ): void {
    this.setMetadata(
      [this.metadata, ...validationMetadata],
      suppressChangeEvent
    );
  }

  private getModelConfigsCore(): ModelMetadata[] {
    return this.metadata
      .map((metadata) =>
        Object.keys(metadata)
          .filter((key) => key !== 'global')
          .map(
            (modelType) =>
              new ModelMetadata(
                this,
                modelType,
                metadata[modelType],
                this.validatorFactories,
                this.getGlobalConfig()
              )
          )
      )
      .reduce((prev, curr) => [...prev, ...curr], []);
  }

  private clear(): void {
    delete this.metadata;
    delete this.modelMetadata;
    delete this.globalMetadata;
  }
}
